/**
 * src/components/spatial/spatialLayerUtils.ts
 *
 * Shared helpers for the individual spatial-object layer components.
 *
 * Reads strictly from the canonical GeoLink property names:
 *
 *   objectType       (kind discriminator)
 *   floorNumber      (1, 2, … or negative for basements)
 *   baseHeightMeters / heightMeters
 *   colorKey         (hex string, used directly as fill colour)
 *   executionRuleId  (link-policy filter)
 *   contractAddress  (link-contract filter)
 *
 * All three providers (mapbox-gl, maplibre-gl, maptiler-sdk) accept
 * the same data-expression syntax, so these helpers double as the
 * provider-agnostic paint spec.
 */

import type { SpatialObjectType } from '../../services/spatial/geoLinkTypes';
import type { SpatialObjectsFilters, SpatialObjectsView } from './SpatialObjectsContext';

export const SPATIAL_SOURCE_ID = 'gl-spatial-objects';

/** XYZ raster source for the optional MapTiler complement layer. */
export const SPATIAL_MAPTILER_RASTER_SOURCE_ID = 'gl-spatial-maptiler-raster';
export const SPATIAL_MAPTILER_RASTER_LAYER_ID  = 'gl-spatial-maptiler-raster-layer';

export const SPATIAL_LAYER_IDS = {
  building: 'gl-spatial-building-extrusion',
  floor:    'gl-spatial-floor-extrusion',
  zone:     'gl-spatial-zone-extrusion',
  slice:    'gl-spatial-slice-extrusion',
  /** Fallback marker for features whose geometry is still a placeholder
   *  Point. Fill-extrusion layers only render Polygon / MultiPolygon, so
   *  Point objects would otherwise be invisible — this circle layer makes
   *  them discoverable so the operator knows to upgrade the geometry. */
  point:    'gl-spatial-point-marker',
} as const;

export type SpatialLayerId = typeof SPATIAL_LAYER_IDS[keyof typeof SPATIAL_LAYER_IDS];

/** Every layer a spatial object can be picked from, in paint order. */
export const ALL_SPATIAL_LAYER_IDS: readonly SpatialLayerId[] = Object.freeze([
  SPATIAL_LAYER_IDS.zone,
  SPATIAL_LAYER_IDS.slice,
  SPATIAL_LAYER_IDS.floor,
  SPATIAL_LAYER_IDS.building,
  SPATIAL_LAYER_IDS.point,
]);

/**
 * The little of a map this hit test needs.
 *
 * Structural rather than `mapboxgl.Map`, because the three providers
 * expose these two methods identically and the caller with the worst
 * problem to solve — `App`'s country handlers — should not have to
 * care which one rendered the layer.
 */
export interface SpatialHitTestMap {
  getLayer(id: string): unknown;
  queryRenderedFeatures(
    geometry?: unknown,
    options?: { layers?: string[] },
  ): unknown[];
}

/**
 * The spatial objects under a point, with room for a fingertip.
 *
 * This exists because the answer has to be the same everywhere it is
 * asked. Anything that must stand down when a tap lands on a building
 * — the country click handlers, the country hover — decides that by
 * hit-testing the spatial layers itself, and two handlers asking the
 * same question two slightly different ways is how a tap opened a
 * country card over the building it was aimed at.
 *
 * `tolerancePx` is the whole point on a phone. A pointer arrives where
 * it was pointed; a fingertip arrives near it. The country path has
 * always padded its own query for that reason, so the yield that runs
 * before it must pad by the same amount or it loses every near miss
 * to the thing it was supposed to yield to.
 *
 * Missing layers are filtered rather than passed through: Mapbox
 * throws on a query naming a layer the style has not added yet, and
 * the spatial layers only exist once GeoLink has answered.
 */
export function spatialFeaturesAt(
  map: SpatialHitTestMap | null | undefined,
  point: { x: number; y: number } | null | undefined,
  tolerancePx = 0,
): unknown[] {
  if (!map || !point || !Number.isFinite(point.x) || !Number.isFinite(point.y)) return [];

  const layers = ALL_SPATIAL_LAYER_IDS.filter((id) => {
    try { return !!map.getLayer(id); } catch { return false; }
  });
  if (layers.length === 0) return [];

  const pad = Number.isFinite(tolerancePx) && tolerancePx > 0 ? tolerancePx : 0;
  const geometry = pad > 0
    ? [
      { x: point.x - pad, y: point.y - pad },
      { x: point.x + pad, y: point.y + pad },
    ]
    : point;

  try {
    const hits = map.queryRenderedFeatures(geometry, { layers: layers as string[] });
    return Array.isArray(hits) ? hits : [];
  } catch {
    // A query that throws is not a hit. Reporting one would make every
    // handler stand down and nothing would respond to the tap at all.
    return [];
  }
}

/**
 * How much slack a tap gets, in pixels.
 *
 * Zero for a pointer and 12–44 px for a fingertip, tightening as the
 * camera comes in because a building fills more of the screen the
 * closer you are. Lifted out of `App`'s country handler rather than
 * reinvented: the yield and the country resolution it guards have to
 * agree about how big a finger is.
 */
export function tapTolerancePx(input: { touch: boolean; zoom?: number }): number {
  if (!input.touch) return 0;
  const zoom = Number.isFinite(input.zoom) ? (input.zoom as number) : 8;
  return Math.max(12, Math.min(44, Math.round(34 - zoom)));
}

/**
 * GeoLink ships several object types; the spatial layers paint
 * `building` / `floor` / `zone` / `slice` natively. The other types
 * (`object_3d`, `real_estate`, `event_object`,
 * `contract_execution_object`) are folded onto the building layer so
 * they still render as 3D extrusions until we add dedicated layers.
 */
export function objectTypeToLayerKind(t: SpatialObjectType): 'building' | 'floor' | 'zone' | 'slice' {
  switch (t) {
    case 'floor': return 'floor';
    case 'zone':  return 'zone';
    case 'slice': return 'slice';
    // A surface is a face rather than a volume, so it paints flat with
    // the zones rather than extruding a second shell over its building.
    case 'surface_zone': return 'zone';
    case 'building':
    case 'object_3d':
    case 'real_estate':
    case 'event_object':
    case 'contract_execution_object':
    default:
      return 'building';
  }
}

/**
 * Build the filter expression for a given layer kind. Combines:
 *   - the objectType match (with the building layer absorbing any
 *     non-floor/zone/slice type for forward compatibility),
 *   - the optional floor selector (matches `floorNumber`),
 *   - the link-toggle filters (executionRuleId / contractAddress),
 *   - the slice-mode visibility rule.
 */
export function buildKindFilter(
  kind: 'building' | 'floor' | 'zone' | 'slice',
  view: SpatialObjectsView,
  filters: SpatialObjectsFilters,
): unknown[] {
  const clauses: unknown[] = ['all'];

  // 0. Geometry guard. fill-extrusion ONLY paints Polygon / MultiPolygon.
  //    Mapbox-gl-js 3.x crashes with `Cannot read properties of undefined`
  //    inside draw_fill_extrusion when a non-polygon feature passes the
  //    layer filter, so we MUST exclude points (placeholder geometries
  //    are routed to SpatialObjectPointLayer's circle layer instead).
  clauses.push([
    'any',
    ['==', ['geometry-type'], 'Polygon'],
    ['==', ['geometry-type'], 'MultiPolygon'],
  ]);

  // 1. objectType match.
  if (kind === 'building') {
    // Catch every non-floor/zone/slice type on the building layer.
    clauses.push([
      'all',
      ['!=', ['get', 'objectType'], 'floor'],
      ['!=', ['get', 'objectType'], 'zone'],
      ['!=', ['get', 'objectType'], 'slice'],
    ]);
  } else {
    clauses.push(['==', ['get', 'objectType'], kind]);
  }

  // 2. Floor selector (only constrains floor / zone, not building / slice).
  if (view.selectedFloorNumber != null && (kind === 'floor' || kind === 'zone')) {
    clauses.push(['==', ['get', 'floorNumber'], view.selectedFloorNumber]);
  }

  // 3. Slice mode hides floor + zone, slice-only shows in slice mode.
  if (view.sliceMode) {
    if (kind === 'floor' || kind === 'zone') {
      clauses.push(['==', ['get', 'objectType'], '__never__']);
    }
  } else if (kind === 'slice') {
    clauses.push(['==', ['get', 'objectType'], '__never__']);
  }

  // 4. Link-toggle filters. Both clauses use canonical fields directly,
  //    plus the GeoTrust-derived boolean flags (set in the root layer)
  //    as a fast path. Mapbox/MapLibre have no `is null` operator, so
  //    we negate the boolean flag (linkHasExecutionRule === true).
  if (filters.policyLinkedOnly) {
    clauses.push([
      'any',
      ['==', ['get', 'linkHasExecutionRule'], true],
      ['!=', ['coalesce', ['get', 'executionRuleId'], null], null],
    ]);
  }
  if (filters.contractLinkedOnly) {
    clauses.push([
      'any',
      ['==', ['get', 'linkHasContract'], true],
      ['!=', ['coalesce', ['get', 'contractAddress'], null], null],
    ]);
  }
  if (filters.eventsOnly) {
    clauses.push(['==', ['get', 'linkHasEvent'], true]);
  }

  return clauses;
}

/**
 * Colour for an occasion that is happening now.
 *
 * Deliberately unlike any of the type fallbacks: the point of the state is
 * that it catches the eye from across the map, which a slightly different
 * blue would not.
 */
export const EVENT_LIVE_COLOR = '#f59e0b';

/**
 * Colour for an occasion that has not started yet.
 *
 * A paler amber, so "on now" and "on later" are the same family and still
 * tell apart at a glance.
 */
export const EVENT_SOON_COLOR = '#fcd34d';

/**
 * Colour for the object currently being pointed at.
 *
 * Outside the amber family on purpose. "On now" and "the one I asked
 * about" are different claims, and a highlight in the same family
 * would read as a third occasion state — so a violet that belongs to
 * the event panel rather than to the clock.
 */
export const EVENT_HIGHLIGHT_COLOR = '#a855f7';

/**
 * `fill-extrusion-base` data expression. Reads canonical
 * `baseHeightMeters` (always present per the GeoLink envelope) and
 * lifts each floor by `(floorNumber - 1) × explodeGapMeters` when
 * explode mode is on.
 */
export function buildExtrusionBaseExpr(view: SpatialObjectsView, kind: 'building' | 'floor' | 'zone' | 'slice'): unknown {
  const baseExpr: unknown = ['coalesce', ['get', 'baseHeightMeters'], ['get', 'base_height'], 0];

  if (!view.explodeFloors || (kind !== 'floor' && kind !== 'zone')) {
    return baseExpr;
  }

  return [
    '+',
    baseExpr,
    [
      '*',
      ['-', ['coalesce', ['get', 'floorNumber'], 1], 1],
      view.explodeGapMeters,
    ],
  ];
}

/** Matching `fill-extrusion-height` — height tracks the explode shift. */
export function buildExtrusionHeightExpr(view: SpatialObjectsView, kind: 'building' | 'floor' | 'zone' | 'slice'): unknown {
  const heightExpr: unknown = ['coalesce', ['get', 'heightMeters'], ['get', 'height'], 1];

  if (!view.explodeFloors || (kind !== 'floor' && kind !== 'zone')) {
    return heightExpr;
  }

  return [
    '+',
    heightExpr,
    [
      '*',
      ['-', ['coalesce', ['get', 'floorNumber'], 1], 1],
      view.explodeGapMeters,
    ],
  ];
}

/**
 * Color expression. GeoLink's canonical `colorKey` is a hex string
 * (e.g. "#4A90E2"); the `fill_color` provider alias is the same value
 * under a snake_case key. We coalesce both so the same code paints
 * regardless of which `?provider=` was requested.
 */
export function buildExtrusionColorExpr(kind: 'building' | 'floor' | 'zone' | 'slice'): unknown {
  const fallback =
    kind === 'building' ? '#6366f1' :
    kind === 'floor'    ? '#3b82f6' :
    kind === 'zone'     ? '#22c55e' :
    /* slice */            '#ef4444';
  const configured: unknown = ['coalesce', ['get', 'colorKey'], ['get', 'fill_color'], fallback];

  // An occasion overrides the object's own colour — except on a building
  // already wearing a flag, where the flag *is* the occasion and washing
  // it amber would undo the thing that was switched on. `past` is
  // deliberately not styled: an occasion that is over should look like
  // any other building again.
  const notFlagged: unknown = ['!', ['has', 'flagCountryCode']];
  return [
    'case',
    // First, and above the flag rule: the object somebody is currently
    // being shown. An event panel that asked the map to point at a
    // building has to be answered visibly, or "show me the stage"
    // moves the camera and marks nothing.
    ['==', ['get', 'eventHighlighted'], true], EVENT_HIGHLIGHT_COLOR,
    ['all', ['==', ['get', 'eventState'], 'live'], notFlagged], EVENT_LIVE_COLOR,
    ['all', ['==', ['get', 'eventState'], 'soon'], notFlagged], EVENT_SOON_COLOR,
    configured,
  ];
}

/**
 * Opacity per layer kind. The building value is intentionally high
 * (0.85) because the layer paints on top of Mapbox's gray
 * `3d-buildings` extrusion at the same footprint — anything lower
 * blends with the gray underneath and looks washed out, which
 * historically read as "not rendering" to operators. (GeoLink doesn't
 * ship per-feature opacity today.)
 */
export function buildExtrusionOpacityExpr(kind: 'building' | 'floor' | 'zone' | 'slice'): number {
  return kind === 'building' ? 0.85 :
         kind === 'floor'    ? 0.7  :
         kind === 'zone'     ? 0.85 :
         /* slice */            0.18;
}
